import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import { Typography } from "@progress/kendo-react-common";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { Button } from "@progress/kendo-react-buttons";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import FormTextField from "../../components/formFields/FormTextField";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import ShadowCard from "../../components/common/ShadowCard";

import {
  createShippingMovements,
  getShippingMovementsByID,
  updateShippingMovements,
} from "./services/shippingMovementsList.services";

import { clearShippingMovementsDetails } from "./shippingMovementsListSlice";
import FormDatePicker from "../../components/formFields/FormDateField";
import { closeDialog } from "../../components/dialog/dialogSlice";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { getAllActiveNewPort } from "../newport/services/newport.services";
import { getAllActiveAgent } from "../agent/services/agent.services";
import { getAllActiveLine } from "../line/services/line.services";
import { INewPort } from "../newport/newportModel";
import { ILine } from "../line/lineModel";
import { IAgent } from "../agent/agentModel";
import FormTextArea from "../../components/formFields/FormTextArea";
import FormMultiSelectionFiled from "../../components/formFields/FormMultiSelectionFiled";
import moment from "moment";
import FormTimePicker from "../../components/formFields/FormTimeField";

const CreateShippingMovementsList: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const shipping_guid = location.state?.shipping_guid;
  const gridRef = useRef<any>(null);
  const dispatch = useAppDispatch();
  const loading = useAppSelector(
    (state) => state.shippingMovementslist.loading
  );
  const AgentList = useAppSelector((state) => state.agent.AgentList);
  const LineList = useAppSelector((state) => state.line.LineList);
  const PortList = useAppSelector((state) => state.newport.NewPortList);

  const ShippingMovementsDetail = useAppSelector(
    (state) => state.shippingMovementslist.ShippingMovementsDetail
  );
  const [formKey, setFormKey] = useState(1);

  useEffect(() => {
    dispatch(getAllActiveNewPort());
    dispatch(getAllActiveAgent());
    dispatch(getAllActiveLine());
    return () => {
      dispatch(clearShippingMovementsDetails());
      dispatch(closeDialog());
    };
  }, []);

  useEffect(() => {
    if (shipping_guid) {
      dispatch(getShippingMovementsByID(shipping_guid));
    }
  }, [shipping_guid]);

  useEffect(() => {
    setFormKey(formKey + 1);
  }, [ShippingMovementsDetail, shipping_guid]);

  const handleSubmit = async (values: any) => {
    const formdata = {
      // form_type:"SHIPPING",
      port_id: values?.port_id,
      eta: values?.eta ? moment(values?.eta).format("YYYY-MM-DD") : "",
      cut_off_datetime: values?.cut_off_datetime
        ? moment(values?.cut_off_datetime).format("YYYY-MM-DD")
        : "",
      cut_off_time: values?.cut_off_time
        ? moment(values?.cut_off_time).format("HH:mm:ss")
        : "",
      vessels_name: values?.vessels_name,
      voj_no: values?.voj_no,
      vcn: values?.vcn,
      line_id: values?.line_id.join(","),
      agent_id: values?.agent_id.join(","),
      will_load_for: values?.will_load_for,
      etd: values?.etd ? moment(values?.etd).format("YYYY-MM-DD") : "",
    };
    if (shipping_guid) {
      try {
        const updatePayload = { shipping_guid: shipping_guid, ...formdata };

        const response = await dispatch(updateShippingMovements(updatePayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          navigate("/shippingmovements");
        }
      } catch (error) {
        console.error("Error in handleSubmit:", error);
        throw error;
      }
    } else {
      try {
        const response = await dispatch(createShippingMovements(formdata));
        if (response?.meta?.requestStatus === "fulfilled") {
          navigate("/shippingmovements");
        }
      } catch (error) {
        console.error("Error in handleSubmit:", error);
        throw error;
      }
    }
  };

  if (loading) return <LoadingPanel gridRef={gridRef} />;

  return (
    <>
      <Form
        key={formKey}
        onSubmit={handleSubmit}
        initialValues={ShippingMovementsDetail}
        render={(formRenderProps: FormRenderProps) => (
          <FormElement>
            <ShadowCard style={{ padding: 12 }}>
              <GridLayout
                style={{ marginRight: 30 }}
                gap={{ rows: 10, cols: 10 }}
                cols={[
                  { width: "25%" },
                  { width: "25%" },
                  { width: "25%" },
                  { width: "25%" },
                ]}
              >
                <GridLayoutItem colSpan={4}>
                  <Typography.h4>
                    {shipping_guid
                      ? "Update Shipping Movement"
                      : "Add Shipping Movement"}
                  </Typography.h4>
                </GridLayoutItem>

                <GridLayoutItem>
                  <Field
                    name="port_id"
                    label="Port"
                    placeholder="Select Port"
                    component={FormSelectionField}
                    options={PortList?.map((port: INewPort) => {
                      return {
                        value: port.id,
                        label: port.port_name,
                      };
                    })}
                    validator={requiredValidator}
                    astrike={true}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="eta"
                    label="ETA"
                    component={FormDatePicker}
                    validator={requiredValidator}
                    astrike={true}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="cut_off_datetime"
                    label="Cut Off Date"
                    format="dd/MM/yyyy"
                    component={FormDatePicker}
                    validator={requiredValidator}
                    astrike={true}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="cut_off_time"
                    label="Cut Off Time"
                    component={FormTimePicker}
                    validator={requiredValidator}
                    astrike={true}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="vessels_name"
                    label="Vessel's Name"
                    placeholder="Vessels Name"
                    component={FormTextField}
                    validator={requiredValidator}
                    astrike={true}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="voj_no"
                    label="VOY No"
                    placeholder="123456AB9"
                    component={FormTextField}
                    validator={requiredValidator}
                    astrike={true}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="vcn"
                    label="VCN"
                    placeholder="1234 5678 9012 3456"
                    component={FormTextField}
                    validator={requiredValidator}
                    astrike={true}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="line_id"
                    label="Line"
                    placeholder="Select Line"
                    component={FormMultiSelectionFiled}
                    options={LineList?.map((line: ILine) => {
                      return {
                        value: line.id,
                        label: line.line_name,
                      };
                    })}
                    validator={requiredValidator}
                    astrike={true}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="agent_id"
                    label="Agent"
                    placeholder="Select Agent"
                    component={FormMultiSelectionFiled}
                    options={AgentList?.map((agent: IAgent) => {
                      return {
                        value: agent.id,
                        label: agent.agent_name,
                      };
                    })}
                    validator={requiredValidator}
                    astrike={true}
                  />
                </GridLayoutItem>

                <GridLayoutItem>
                  <Field
                    name="will_load_for"
                    label="Will Load For"
                    placeholder="Write..."
                    component={FormTextArea}
                    rows={2}
                    validator={requiredValidator}
                    astrike={true}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="etd"
                    label="ETD"
                    component={FormDatePicker}
                    validator={requiredValidator}
                    astrike={true}
                  />
                </GridLayoutItem>

                <GridLayoutItem
                  colSpan={4}
                  style={{
                    textAlign: "end",
                    marginTop: "20px",
                    width: "100%",
                  }}
                >
                  <ButtonWithLoading
                    label={shipping_guid ? "Update" : "Save"}
                    type="submit"
                    disabled={!formRenderProps.allowSubmit || loading}
                    loading={loading}
                  />
                  <Button
                    type="button"
                    fillMode={"outline"}
                    themeColor={"primary"}
                    style={{ marginLeft: 4 }}
                    onClick={() => navigate("/shippingmovements")}
                  >
                    Cancel
                  </Button>
                </GridLayoutItem>
              </GridLayout>
            </ShadowCard>
          </FormElement>
        )}
      />
    </>
  );
};

export default CreateShippingMovementsList;
